import React, { useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import MediaPickerModal from "./MediaPickerModal";

const buildMapEmbedUrl = (input) => {
  const value = input.trim();
  const coordPair = /^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/;
  const atCoords = /[@!](-?\d{1,3}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)(?:,(\d+(?:\.\d+)?)z)?/;
  const qParam = /[?&]q=([^&]+)/;

  const direct = value.match(coordPair);
  if (direct) {
    return `https://maps.google.com/maps?q=${direct[1]},${direct[2]}&hl=en&z=15&output=embed`;
  }

  const at = value.match(atCoords);
  if (at) {
    const zoom = at[3] ? Math.round(parseFloat(at[3])) : 15;
    return `https://maps.google.com/maps?q=${at[1]},${at[2]}&hl=en&z=${zoom}&output=embed`;
  }

  const q = value.match(qParam);
  if (q) {
    return `https://maps.google.com/maps?q=${q[1]}&output=embed`;
  }

  return `https://maps.google.com/maps?q=${encodeURIComponent(value)}&output=embed`;
};

const TinyMceEditor = ({ value, onChange, editorRef, height = 400 }) => {
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const internalEditorRef = useRef(null);

  const handleMediaSelect = (url) => {
    const editor = internalEditorRef.current;
    if (editor && url) {
      editor.insertContent(`<img src="${url}" alt="" />`);
    }
    setShowMediaPicker(false);
  };

  return (
    <>
      <Editor
        tinymceScriptSrc="https://cdn.tiny.cloud/1/23ecdyf4j6wrgb6682fhy7sr995s8sf363y9vzh5wn4n0loj/tinymce/6/tinymce.min.js"
        value={value}
        onEditorChange={onChange}
        onInit={(evt, editor) => {
          internalEditorRef.current = editor;
          if (editorRef) editorRef.current = editor;
        }}
        init={{
          height,
          menubar: "edit view format insert map",
          branding: false,
          promotion: false,
          plugins: "link lists code table media",
          toolbar: "bullist numlist | table",
          statusbar: false,
          skin: "oxide",
          content_css: "default",
          extended_valid_elements:
            "iframe[src|title|width|height|frameborder|allowfullscreen|loading|referrerpolicy|style]",
          object_resizing: "img,table,iframe",
          menu: {
            insert: {
              title: "Insert",
              items: "mediapicker link | inserttable | hr",
            },
            map: {
              title: "Map",
              items: "insertmap",
            },
          },
          setup: (editor) => {
            editor.ui.registry.addMenuItem("mediapicker", {
              icon: "image",
              text: "Image...",
              onAction: () => setShowMediaPicker(true),
            });

            editor.ui.registry.addMenuItem("insertmap", {
              text: "Insert Map Location...",
              onAction: () => {
                editor.windowManager.open({
                  title: "Insert Map",
                  body: {
                    type: "panel",
                    items: [
                      {
                        type: "input",
                        name: "location",
                        label: "Paste Google Maps URL, lat,lng, or place name",
                      },
                    ],
                  },
                  buttons: [
                    { type: "cancel", text: "Cancel" },
                    {
                      type: "submit",
                      text: "Insert",
                      primary: true,
                    },
                  ],
                  onSubmit: (api) => {
                    const { location } = api.getData();
                    const trimmed = (location || "").trim();
                    if (!trimmed) {
                      api.close();
                      return;
                    }
                    const src = buildMapEmbedUrl(trimmed);
                    editor.insertContent(
                      `<p><iframe src="${src}" width="100%" height="400" style="border:0;width:100%;" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen="allowfullscreen"></iframe></p>`
                    );
                    api.close();
                  },
                });
              },
            });
          },
        }}
      />
      <MediaPickerModal
        show={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleMediaSelect}
      />
    </>
  );
};

export default TinyMceEditor;
